/* Grievance Inbox — the handler side.

   Reads the same records as My Grievances through the same endpoint with
   `scope=inbox`; the API decides what this caller may see (sensitive
   categories, anonymity masking) and returns `can_handle` per row, so this
   screen renders its action panel from the server's answer rather than
   re-deriving the rules here and drifting from them. */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Select } from "antd";
import {
    AlertTriangle, ArrowUpCircle, CheckCircle2, Eye, HelpCircle, Inbox,
    Lock, Search, Send, ShieldAlert, UserCheck,
} from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import { Textarea } from "../../components/ui/input";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { cn } from "../../utils/tailwindUtils";
import {
    EMPLOYEE_PERSONAL_INFO, GRIEVANCE_ACKNOWLEDGE, GRIEVANCE_ASSIGN,
    GRIEVANCE_COMMENTS, GRIEVANCE_ESCALATE, GRIEVANCE_INVESTIGATE,
    GRIEVANCE_REQUEST_INFO, GRIEVANCE_RESOLVE, GRIEVANCES,
} from "../../constants/api";
import { GRIEVANCE_STATUSES, RESOLUTION_TYPES } from "../../constants/grievance";
import { truncateText } from "../../components/common/CommonFunctions";
import {
    AttachmentList, DetailRows, employeeDropdownRows, escalationLevelText, Field, fmtDate,
    fmtDateTime, formatEmployeeOptionLabel, GrievanceHeader, PriorityBadge, SectionLabel,
    SlaBadge, StatusBadge, Thread, Timeline, TruncatedBlock,
} from "./GrievancePieces";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const QUEUE_TABS = [
    { value: "open", label: "Open" },
    { value: "breached", label: "Overdue" },
    { value: "mine", label: "Assigned to me" },
    { value: "", label: "All" },
];

/* Which handler actions the current status allows. Mirrors
   grievance/state_machine.py:TRANSITIONS — the server rejects anything else
   with a 409, so this only decides which buttons are worth showing. */
const ACTIONS_BY_STATUS = {
    submitted: ["acknowledge", "assign", "escalate"],
    acknowledged: ["investigate", "request_info", "resolve", "escalate", "assign"],
    under_investigation: ["request_info", "resolve", "escalate", "assign"],
    awaiting_employee_response: ["escalate", "assign"],
    escalated: ["investigate", "request_info", "resolve", "escalate", "assign"],
    reopened: ["acknowledge", "investigate", "resolve", "escalate", "assign"],
    resolved: [],
    closed: [],
    withdrawn: [],
};

const GrievanceInbox = () => {
    const { fetch, create } = useApiMethods();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [tab, setTab] = useState("open");
    const [detailId, setDetailId] = useState(null);

    useEffect(() => {
        const linked = searchParams.get("id") || location.state?.grievanceId;
        if (linked) {
            const numericId = Number(linked);
            if (Number.isFinite(numericId) && numericId > 0) {
                setDetailId(numericId);
                setTab("");
            }
        }
    }, [searchParams, location.state]);
    const { insertBranchColumn } = useOrgAdminBranchColumn("employee_name");

    const refresh = () => setServerTrigger((t) => !t);

    /* One tab maps onto one server filter; the component hashes `filter` by
       content, so rebuilding the object each render is fine. */
    const filter = {
        scope: "inbox",
        ...(tab === "open" ? { grievance_status: "open" } : {}),
        ...(tab === "breached" ? { breached: "true" } : {}),
        ...(tab === "mine" ? { assigned_to_me: "true" } : {}),
    };

    const columns = insertBranchColumn([
        {
            accessor: "code", Header: "Grievance", filterable: true, sortable: true,
            Cell: ({ value }) => <span className="font-bold text-sidebar-primary">{value ?? "-"}</span>,
        },
        {
            accessor: "subject", Header: "Subject", filterable: true,
            Cell: ({ row }) => (
                <div className="flex items-center gap-1.5">
                    {row.is_sensitive && (
                        <Lock className="h-3.5 w-3.5 shrink-0 text-rose-500" title="Restricted category" />
                    )}
                    <span className="font-medium">
                        {truncateText(row.subject || "-", 40, "280px", "balance")}
                    </span>
                </div>
            ),
        },
        {
            accessor: "employee_name", Header: "Raised by",
            Cell: ({ value }) => (
                <span className={cn("text-sm", value === "Anonymous" && "italic text-muted-foreground")}>
                    {value ?? "-"}
                </span>
            ),
        },
        { accessor: "category_name", Header: "Category", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "priority", Header: "Priority",
            Cell: ({ value }) => <PriorityBadge priority={value} />,
        },
        {
            accessor: "status", Header: "Status", filterable: true,
            filterType: "select",
            filterOptions: GRIEVANCE_STATUSES.map((s) => ({ label: s.label, value: s.value })),
            Cell: ({ row }) => (
                <div className="flex flex-wrap items-center gap-1.5">
                    <StatusBadge status={row.status} resolutionType={row.resolution_type} />
                    {row.is_breached && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:bg-red-900/30 dark:text-red-300">
                            <AlertTriangle className="h-3 w-3" /> Overdue
                        </span>
                    )}
                </div>
            ),
        },
        { accessor: "assigned_to_name", Header: "Handler", Cell: ({ value }) => value ?? "Unassigned" },
        {
            accessor: "resolution_due_at", Header: "Due", sortable: true,
            Cell: ({ value }) => <span className="text-xs text-muted-foreground">{fmtDate(value)}</span>,
        },
        {
            accessor: "Action", Header: "",
            Cell: ({ row }) => (
                <Button
                    variant="ghost" size="sm" className="text-sidebar-primary"
                    onClick={(e) => { e.stopPropagation(); setDetailId(row.id); }}
                >
                    <Eye className="h-4 w-4" />
                </Button>
            ),
        },
    ]);

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Inbox}
                title="Grievance Inbox"
                subtitle="Acknowledge, investigate and resolve the grievances routed to you"
                totalRecords={totalRecords}
            />

            <div className="mb-2 px-3">
                <SegmentSwitch
                    options={QUEUE_TABS}
                    value={tab}
                    onChange={setTab}
                />
            </div>

            <Reactable
                pagename={`inbox-${tab || "all"}`}
                columns={columns}
                data={rows}
                setData={setRows}
                setTotalCount={setTotalRecords}
                url={GRIEVANCES}
                filter={filter}
                serverSide
                serverTrigger={serverTrigger}
                isCSVRequired
                isPDFRequired
                tableHeight="calc(100vh - 370px)"
                rowSize="sm"
                onRowClick={(row) => setDetailId(row.id)}
            />

            {detailId && (
                <HandlerDetailModal
                    grievanceId={detailId}
                    fetch={fetch}
                    create={create}
                    onClose={() => {
                        setDetailId(null);
                        if (searchParams.has("id")) {
                            const next = new URLSearchParams(searchParams);
                            next.delete("id");
                            setSearchParams(next, { replace: true });
                        }
                    }}
                    onChanged={refresh}
                />
            )}
        </ContainerCard>
    );
};

/* ---------------------------- handler detail ----------------------------- */

export const HandlerDetailModal = ({ grievanceId, fetch, create, onClose, onChanged }) => {
    const [grievance, setGrievance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const [panel, setPanel] = useState(null);
    const [comment, setComment] = useState("");
    const [resolutionType, setResolutionType] = useState("resolved");
    const [assignee, setAssignee] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [note, setNote] = useState("");
    const [noteInternal, setNoteInternal] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(GRIEVANCES, { id: grievanceId });
            if (res?.status === "success") setGrievance(res.data);
            else ToastMsg("error", res?.message || "Could not load grievance.");
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grievanceId]);

    useEffect(() => { load(); }, [load]);

    /* Employees are only needed once the assign panel opens — loading them
       with every detail view would be a wasted request on most opens. */
    const loadEmployees = useCallback(async () => {
        try {
            const res = await fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" });
            if (res?.status === "success") setEmployees(employeeDropdownRows(res.data));
        } catch (e) {
            ToastMsg("error", e.message);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetPanel = () => {
        setPanel(null);
        setComment("");
        setAssignee(null);
    };

    const act = async (url, payload, successFallback) => {
        setBusy(true);
        try {
            const res = await create(url, { id: grievanceId, ...payload });
            if (res?.status === "success") {
                ToastMsg("success", res.message || successFallback);
                setGrievance(res.data);
                resetPanel();
                onChanged();
            } else {
                ToastMsg("error", res?.message || "Action failed.");
            }
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setBusy(false);
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setBusy(true);
        try {
            const res = await create(GRIEVANCE_COMMENTS, {
                grievance_id: grievanceId,
                comment: note.trim(),
                visibility: noteInternal ? "internal" : "shared",
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Comment added.");
                setNote("");
                await load();
                onChanged();
            } else {
                ToastMsg("error", res?.message || "Could not add comment.");
            }
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading || !grievance) {
        return (
            <DialogModal open onClose={onClose} title="Grievance" showActions={false} width="720px">
                <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
            </DialogModal>
        );
    }

    const available = grievance.can_handle ? (ACTIONS_BY_STATUS[grievance.status] || []) : [];
    const atTopRung = (grievance.escalation_level ?? grievance.sla?.escalation_level ?? 0) >= 2;
    const offered = atTopRung ? available.filter((verb) => verb !== "escalate") : available;

    return (
        <DialogModal
            open
            onClose={onClose}
            title={`Grievance ${grievance.code}`}
            showActions={false}
            width="720px"
        >
            <div className="space-y-4">
                <GrievanceHeader grievance={grievance} />

                {grievance.category?.is_sensitive && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/40 dark:bg-rose-900/20">
                        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-rose-600 dark:text-rose-300" />
                        <p className="text-xs text-rose-800 dark:text-rose-200">
                            Restricted category. Do not discuss this record outside the
                            designated handler and escalation contact.
                        </p>
                    </div>
                )}

                {grievance.is_anonymous && (
                    <div className="flex items-start gap-2.5 rounded-lg border border-purple-200 bg-purple-50 p-3 dark:border-purple-900/40 dark:bg-purple-900/20">
                        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-purple-600 dark:text-purple-300" />
                        <p className="text-xs text-purple-800 dark:text-purple-200">
                            Raised anonymously. You cannot see who filed it, and replies you
                            post reach them without revealing their identity.
                        </p>
                    </div>
                )}

                <div>
                    <SectionLabel>What happened</SectionLabel>
                    <TruncatedBlock text={grievance.description} />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                        <SectionLabel>Record</SectionLabel>
                        <DetailRows
                            rows={[
                                ["Raised by", grievance.employee?.name],
                                ["Department", grievance.employee?.department],
                                ["Category", grievance.category?.name],
                                ["Incident date", grievance.incident_date ? fmtDate(grievance.incident_date) : "—"],
                                ["Location", grievance.location],
                                ["People involved", grievance.persons_involved?.note],
                            ]}
                        />
                    </div>
                    <div>
                        <SectionLabel>Handling</SectionLabel>
                        <DetailRows
                            rows={[
                                ["Handler", grievance.assigned_to?.name || "Unassigned"],
                                ["Acknowledged", fmtDateTime(grievance.acknowledged_at)],
                                ["Ack due", fmtDateTime(grievance.sla?.ack_due_at)],
                                ["Resolution due", fmtDateTime(grievance.sla?.resolution_due_at)],
                                ["Escalation level", escalationLevelText(grievance.escalation_level ?? grievance.sla?.escalation_level)],
                                ["Reopened", grievance.reopen_count ? `${grievance.reopen_count} time(s)` : "—"],
                            ]}
                        />
                        <div className="mt-2">
                            <SlaBadge sla={grievance.sla} />
                        </div>
                    </div>
                </div>

                {Array.isArray(grievance.attachments) && grievance.attachments.length > 0 && (
                    <div>
                        <SectionLabel>Attachments</SectionLabel>
                        <AttachmentList attachments={grievance.attachments} />
                    </div>
                )}

                <div>
                    <SectionLabel>Conversation</SectionLabel>
                    <Thread comments={grievance.comments} />
                    {grievance.can_handle && !["closed", "withdrawn"].includes(grievance.status) && (
                        <div className="mt-2.5 space-y-2">
                            <Textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                rows={3}
                                placeholder={noteInternal
                                    ? "Internal note — the employee will not see this"
                                    : "Message to the employee"}
                                disabled={busy}
                            />
                            <div className="flex items-center justify-between gap-3">
                                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <input
                                        type="checkbox"
                                        checked={noteInternal}
                                        onChange={(e) => setNoteInternal(e.target.checked)}
                                        disabled={busy}
                                    />
                                    Internal note (not visible to the employee)
                                </label>
                                <Button size="sm" disabled={busy || !note.trim()} onClick={addNote}>
                                    <Send className="h-4 w-4" /> Add
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <div>
                    <SectionLabel>Timeline</SectionLabel>
                    <Timeline entries={grievance.audit_trail} />
                </div>

                {/* ------------------------- action panels ------------------------- */}

                {panel === "acknowledge" && (
                    <ActionPanel
                        tone="blue"
                        label="Acknowledge this grievance"
                        hint="Optional note. Acknowledging claims ownership and stops the acknowledgement SLA clock."
                        value={comment}
                        onChange={setComment}
                        onCancel={resetPanel}
                        onSubmit={() => act(GRIEVANCE_ACKNOWLEDGE, { comment: comment.trim() }, "Acknowledged.")}
                        submitLabel="Acknowledge"
                        busy={busy}
                    />
                )}

                {panel === "investigate" && (
                    <ActionPanel
                        tone="indigo"
                        label="Start a formal investigation"
                        hint="Optional note recorded on the timeline."
                        value={comment}
                        onChange={setComment}
                        onCancel={resetPanel}
                        onSubmit={() => act(GRIEVANCE_INVESTIGATE, { comment: comment.trim() }, "Investigation started.")}
                        submitLabel="Start investigation"
                        busy={busy}
                    />
                )}

                {panel === "request_info" && (
                    <ActionPanel
                        tone="amber"
                        label="What do you need from the employee?"
                        hint="This is posted to the shared thread, so write it as a question to them."
                        value={comment}
                        onChange={setComment}
                        onCancel={resetPanel}
                        onSubmit={() => act(GRIEVANCE_REQUEST_INFO, { comment: comment.trim() }, "Information requested.")}
                        submitLabel="Request information"
                        busy={busy}
                        requireValue
                    />
                )}

                {panel === "escalate" && (
                    <ActionPanel
                        tone="orange"
                        label="Reason for escalation"
                        hint="The grievance moves one level up and is reassigned to that level."
                        value={comment}
                        onChange={setComment}
                        onCancel={resetPanel}
                        onSubmit={() => act(GRIEVANCE_ESCALATE, { comment: comment.trim() }, "Escalated.")}
                        submitLabel="Escalate"
                        busy={busy}
                        requireValue
                        destructive
                    />
                )}

                {panel === "resolve" && (
                    <div className="space-y-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900/40 dark:bg-emerald-900/10">
                        <Field label="Outcome" required>
                            <Select
                                size="large"
                                className="w-full"
                                value={resolutionType}
                                onChange={setResolutionType}
                                options={RESOLUTION_TYPES.map((r) => ({ label: r.label, value: r.value }))}
                                disabled={busy}
                            />
                        </Field>
                        <Field
                            label="What action was taken?"
                            required
                            hint="The employee sees this, and it is the record an audit reads. Be specific."
                        >
                            <Textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                rows={4}
                                disabled={busy}
                            />
                        </Field>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={resetPanel} disabled={busy}>Cancel</Button>
                            <Button
                                size="sm"
                                disabled={busy || comment.trim().length < 10}
                                onClick={() => act(
                                    GRIEVANCE_RESOLVE,
                                    { resolution: comment.trim(), resolution_type: resolutionType },
                                    "Grievance resolved.",
                                )}
                            >
                                <CheckCircle2 className="h-4 w-4" /> Resolve
                            </Button>
                        </div>
                    </div>
                )}

                {panel === "assign" && (
                    <div className="space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
                        <Field label="Assign to" required>
                            <Select
                                size="large"
                                className="w-full"
                                value={assignee}
                                onChange={setAssignee}
                                placeholder="Select an employee"
                                showSearch
                                optionFilterProp="label"
                                options={employees.map((e) => ({
                                    label: formatEmployeeOptionLabel(e),
                                    value: e.id,
                                }))}
                                disabled={busy}
                            />
                        </Field>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={resetPanel} disabled={busy}>Cancel</Button>
                            <Button
                                size="sm"
                                disabled={busy || !assignee}
                                onClick={() => act(GRIEVANCE_ASSIGN, { assigned_to: assignee }, "Assigned.")}
                            >
                                <UserCheck className="h-4 w-4" /> Assign
                            </Button>
                        </div>
                    </div>
                )}

                {/* --------------------------- footer --------------------------- */}

                <div className="flex flex-wrap justify-end gap-2.5 border-t border-gray-100 pt-4 dark:border-gray-800">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>

                    {!panel && !grievance.can_handle && (
                        <span className="self-center text-xs text-muted-foreground">
                            You have read access to this grievance but cannot act on it.
                        </span>
                    )}

                    {!panel && offered.includes("acknowledge") && (
                        <Button onClick={() => setPanel("acknowledge")} disabled={busy}>
                            <CheckCircle2 className="h-4 w-4" /> Acknowledge
                        </Button>
                    )}
                    {!panel && offered.includes("investigate") && (
                        <Button variant="outline" onClick={() => setPanel("investigate")} disabled={busy}>
                            <Search className="h-4 w-4" /> Investigate
                        </Button>
                    )}
                    {!panel && offered.includes("request_info") && (
                        <Button variant="outline" onClick={() => setPanel("request_info")} disabled={busy}>
                            <HelpCircle className="h-4 w-4" /> Request info
                        </Button>
                    )}
                    {!panel && offered.includes("assign") && (
                        <Button
                            variant="outline"
                            onClick={() => { setPanel("assign"); loadEmployees(); }}
                            disabled={busy}
                        >
                            <UserCheck className="h-4 w-4" /> Assign
                        </Button>
                    )}
                    {!panel && offered.includes("escalate") && (
                        <Button variant="outline" onClick={() => setPanel("escalate")} disabled={busy}>
                            <ArrowUpCircle className="h-4 w-4" /> Escalate
                        </Button>
                    )}
                    {!panel && offered.includes("resolve") && (
                        <Button onClick={() => setPanel("resolve")} disabled={busy}>
                            <CheckCircle2 className="h-4 w-4" /> Resolve
                        </Button>
                    )}
                </div>
            </div>
        </DialogModal>
    );
};

const TONES = {
    blue: "border-blue-200 bg-blue-50/60 dark:border-blue-900/40 dark:bg-blue-900/10",
    indigo: "border-indigo-200 bg-indigo-50/60 dark:border-indigo-900/40 dark:bg-indigo-900/10",
    amber: "border-amber-200 bg-amber-50/60 dark:border-amber-900/40 dark:bg-amber-900/10",
    orange: "border-orange-200 bg-orange-50/60 dark:border-orange-900/40 dark:bg-orange-900/10",
};

const ActionPanel = ({
    tone, label, hint, value, onChange, onCancel, onSubmit, submitLabel,
    busy, requireValue = false, destructive = false,
}) => (
    <div className={cn("space-y-2.5 rounded-lg border p-3", TONES[tone] || TONES.blue)}>
        <Field label={label} required={requireValue} hint={hint}>
            <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} disabled={busy} />
        </Field>
        <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button
                variant={destructive ? "destructive" : "default"}
                size="sm"
                disabled={busy || (requireValue && !value.trim())}
                onClick={onSubmit}
            >
                {submitLabel}
            </Button>
        </div>
    </div>
);

export default GrievanceInbox;
